import { useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import type { ChatMessage, ChatTarget } from '../types';
import { chatKey } from '../chatTarget';

function normalizeStoragePath(rawPath: string, bucketSegment: string) {
  if (!rawPath) return rawPath;
  if (rawPath.startsWith('http://') || rawPath.startsWith('https://')) {
    return rawPath.split(`/${bucketSegment}/`)[1] || rawPath;
  }
  return rawPath;
}

export function useSignedUrls(target: ChatTarget, messages: ChatMessage[]) {
  const key = chatKey(target);

  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const [pdfSignedUrls, setPdfSignedUrls] = useState<Record<string, string>>({});

  const fetchedSignedUrlIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    setSignedUrls({});
    setPdfSignedUrls({});
    fetchedSignedUrlIds.current = new Set();
  }, [key]);

  useEffect(() => {
    const imageMessages = messages.filter((m) => m.image_url && !fetchedSignedUrlIds.current.has(m.id));
    if (!imageMessages.length) return;

    imageMessages.forEach(async (m) => {
      fetchedSignedUrlIds.current.add(m.id);
      const storagePath = normalizeStoragePath(m.image_url!, 'chat-images');

      const { data, error } = await supabase.storage.from('chat-images').createSignedUrl(storagePath, 3600);
      if (error) {
        console.error('[EventChat] createSignedUrl failed', { path: m.image_url, error });
        return;
      }
      if (data?.signedUrl) {
        setSignedUrls((prev) => ({ ...prev, [m.id]: data.signedUrl }));
      }
    });
  }, [messages]);

  useEffect(() => {
    const pdfMessages = messages.filter((m) => m.type === 'pdf' && m.file_url && !pdfSignedUrls[m.id]);
    if (!pdfMessages.length) return;

    pdfMessages.forEach(async (m) => {
      try {
        const storagePath = normalizeStoragePath(m.file_url!, 'chat-documents');

        const { data, error } = await supabase.storage.from('chat-documents').createSignedUrl(storagePath, 3600);
        if (error || !data?.signedUrl) return;
        setPdfSignedUrls((prev) => ({ ...prev, [m.id]: data.signedUrl }));
      } catch (err) {
        console.error('[EventChat] createSignedUrl for pdf failed', {
          path: m.file_url,
          err,
        });
      }
    });
  }, [messages, pdfSignedUrls]);

  return { signedUrls, pdfSignedUrls };
}

